import { RoutalTab, RoutalTabs } from '@nx-smartmonkey/ui';
import * as React from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Grid } from '@mui/material';
import * as routing from '../../../../constants/Routing';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { Project } from '../../../../domain/project/Project';
import { intl } from '../../../../helpers/IntGlobalProvider';
import eventsPanel from '../../../../services/events';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';

interface PlanTabsProps {
  activePlan?: PlanFlat;
  activeProject?: Project;
}

type TabsValue = `overview` | `drivers` | `stops` | `tasks`;

export const PlanTabs = ({ activePlan, activeProject }: PlanTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = React.useState<TabsValue>(`overview`);
  const { data: drivers } = useRetrieveActivePlanDrivers();
  const { data: stops } = useRetrieveActivePlanStops();

  const amountOfTasks = stops?.reduce((amount: number, stop) => amount + (stop.tasks ? stop.tasks : []).length, 0);

  useEffect(() => {
    if (location.pathname) {
      if (location.pathname.includes(`overview`)) {
        setValue(`overview`);
      }
      if (location.pathname.includes(`drivers`)) {
        setValue(`drivers`);
      }
      if (location.pathname.includes(`stops`)) {
        setValue(`stops`);
      }
      if (location.pathname.includes(`tasks`)) {
        setValue(`tasks`);
      }
    }
  }, [location.pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: TabsValue) => {
    if (activeProject && activePlan) {
      switch (newValue) {
        case `overview`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject.id).replace(
              `:planId`,
              activePlan.id
            )
          );
          eventsPanel.planMenu(`overview`);
          break;
        case `drivers`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.DRIVERS.replace(`:projectId`, activeProject.id).replace(
              `:planId`,
              activePlan.id
            )
          );
          eventsPanel.planMenu(`drivers`);
          break;
        case `stops`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.STOPS.replace(`:projectId`, activeProject.id).replace(`:planId`, activePlan.id)
          );
          eventsPanel.planMenu(`stops`);
          break;
        case `tasks`:
          navigate(
            routing.PLANNER.PLAN_DETAILS.TASKS.replace(`:projectId`, activeProject.id).replace(`:planId`, activePlan.id)
          );
          // eventsPanel.planMenu(`tasks`);
          break;
      }
    }
  };

  return (
    <Grid style={{ width: `100%`, height: `100%` }}>
      <RoutalTabs
        value={value}
        onChange={(event, newValue) => handleChange(event, newValue as TabsValue)}
        aria-label="styled tabs"
      >
        <RoutalTab label={intl.formatMessage({ id: `planner.plan_details.header.menu.overview` })} value="overview" />
        <RoutalTab
          label={`${intl.formatMessage({ id: `planner.plan_details.header.menu.drivers` })} ${(drivers ?? []).length}`}
          value="drivers"
        />
        <RoutalTab
          label={`${intl.formatMessage({ id: `planner.plan_details.header.menu.stops` })} ${(stops ?? []).length}`}
          value="stops"
        />
        {activeProject?.tasks_enabled && (
          <RoutalTab
            label={`${intl.formatMessage({ id: `planner.plan_details.header.menu.tasks` })} ${amountOfTasks}`}
            value="tasks"
          />
        )}
      </RoutalTabs>
    </Grid>
  );
};
